import { Component, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Nav } from '../nav/nav';
import { TopHeader } from '../top-header/top-header';
import { PaymentListComponent } from './payment-list/payment-list.component';
import { ExpenseListComponent } from './expense/expense-list/expense-list.component';
import { FilterField, FilterModalComponent } from '../core/ui/filter-modal/filter-modal.component';
import { buildYearDropdownOptions } from '../core/constants/app-constants';
import { AccessControlService, AppUserService, SubscriptionService, SubscriptionUsageSummary } from '../core/services';
import { TranslatePipe } from '../core/pipes/translate.pipe';
import { MONTH_TRANSLATION_KEYS } from '../core/i18n/app-translations';

type PaymentTab = 'Transaction' | 'Expense';

@Component({
  selector: 'app-transaction',
  standalone: true,
  imports: [CommonModule, MatSnackBarModule, Nav, TopHeader, PaymentListComponent, ExpenseListComponent, FilterModalComponent, TranslatePipe],
  templateUrl: './transaction.html',
  styleUrls: ['./transaction.css', './../../CSSFiles/Tabs.css', './../../CSSFiles/BTN_Style.css']
})
export class TransactionComponent implements OnDestroy {
  @ViewChild(PaymentListComponent) paymentList?: PaymentListComponent;
  @ViewChild(ExpenseListComponent) expenseList?: ExpenseListComponent;

  activeTab: PaymentTab = 'Transaction';
  showFilter = false;
  paymentFilterValues: Record<string, any> = {};
  expenseFilterValues: Record<string, any> = {};
  paymentFilterActive = false;
  expenseFilterActive = false;
  isRenterRole = false;
  subscriptionSummary: SubscriptionUsageSummary | null = null;
  private renterId: number | null = null;
  private summaryRefreshSubscription?: Subscription;

  private readonly yearOptions = buildYearDropdownOptions();
  private readonly monthOptions = MONTH_TRANSLATION_KEYS.map((label, i) => ({
    value: i + 1,
    label
  }));

  constructor(
    private readonly access: AccessControlService,
    private readonly appUsers: AppUserService,
    private readonly subscriptionService: SubscriptionService,
    private readonly snackBar: MatSnackBar
  ) {
    this.access.ensureLoaded().subscribe({
      next: () => {
        if (!this.canShowTab(this.activeTab)) {
          this.activeTab = this.canShowTab('Transaction') ? 'Transaction' : 'Expense';
        }
      }
    });

    this.appUsers.me().subscribe({
      next: (profile) => {
        this.isRenterRole = Number(profile?.role) === 4;
        this.renterId = Number(profile?.renterId);
        if (!Number.isFinite(this.renterId)) {
          this.renterId = null;
        }
        this.loadSubscriptionSummary();
        this.summaryRefreshSubscription?.unsubscribe();
        this.summaryRefreshSubscription = this.subscriptionService.summaryRefresh$.subscribe(() => {
          this.loadSubscriptionSummary();
        });
      },
      error: () => {
        this.isRenterRole = false;
        this.renterId = null;
        this.subscriptionSummary = null;
      }
    });
  }

  get paymentFilterFields(): FilterField[] {
    return this.isRenterRole ? this.renterPaymentFilterFields : this.fullPaymentFilterFields;
  }

  private readonly fullPaymentFilterFields: FilterField[] = [
    {
      key: 'flatRenterMapping.flat.house.id',
      label: 'filter.house',
      type: 'select',
      placeholder: 'placeholder.selectHouse',
      dropdownObj: {
        apiInfo: 'HOUSE_LIST',
        name: 'flatRenterMapping.flat.house.id',
        valueKey: 'id',
        display1: 'houseName',
        display2: 'houseAddress',
        label: 'filter.house',
        placeholder: 'placeholder.selectHouse',
        isSearchable: true,
        status: 1
      }
    },
    {
      key: 'flatRenterMapping.flat.id',
      label: 'filter.flat',
      type: 'select',
      placeholder: 'placeholder.selectFlat',
      dependsOnKey: 'flatRenterMapping.flat.house.id',
      dependsOnFilterKey: 'house.id',
      dropdownObj: {
        apiInfo: 'FLAT_LIST',
        name: 'flatRenterMapping.flat.id',
        valueKey: 'id',
        display1: 'flatName',
        label: 'filter.flat',
        placeholder: 'placeholder.selectFlat',
        isSearchable: true,
        status: 1
      }
    },
    {
      key: 'flatRenterMapping.renter.id',
      label: 'filter.renter',
      type: 'select',
      placeholder: 'placeholder.selectRenter',
      dependsOnKey: 'flatRenterMapping.flat.id',
      dependsOnFilterKey: 'flat.id',
      dropdownObj: {
        apiInfo: 'PAYMENT_PENDING_LIST',
        name: 'flatRenterMapping.renter.id',
        valueKey: 'renter.id',
        display1: 'renter.renterName',
        display2: 'renter.renterMobile',
        label: 'filter.renter',
        placeholder: 'placeholder.selectRenter',
        isSearchable: true,
        status: 1
      }
    },
    {
      key: 'txnNo',
      label: 'filter.transactionNo',
      type: 'text',
      placeholder: 'placeholder.typeTransactionNo',
      numericOnly: true,
      exactMatch: true
    },
    {
      key: 'paymentYear',
      label: 'filter.paymentYear',
      type: 'select',
      placeholder: 'placeholder.selectYear',
      dropdownObj: {
        name: 'paymentYear',
        valueKey: 'value',
        display1: 'label',
        label: 'filter.paymentYear',
        options: this.yearOptions,
        isSearchable: true
      }
    },
    {
      key: 'paymentMonth',
      label: 'filter.paymentMonth',
      type: 'select',
      placeholder: 'placeholder.selectMonth',
      dropdownObj: {
        name: 'paymentMonth',
        valueKey: 'value',
        display1: 'label',
        label: 'filter.paymentMonth',
        options: this.monthOptions,
        isSearchable: true,
        searchReadonly: true
      }
    },
    {
      key: 'paymentMethod',
      label: 'filter.paymentMethod',
      type: 'radio',
      options: [
        { value: 1, label: 'paymentMethod.cash' },
        { value: 2, label: 'paymentMethod.bank' },
        { value: 3, label: 'paymentMethod.cheque' },
        { value: 4, label: 'paymentMethod.mobileBanking' },
        { value: 5, label: 'paymentMethod.adjustFromAdvance' },
        { value: 6, label: 'paymentMethod.other' }
      ]
    },
    {
      key: 'paymentDate',
      label: 'filter.paymentDateRange',
      type: 'dateRange'
    },
    {
      key: 'createdOn',
      label: 'filter.createdOnRange',
      type: 'dateRange'
    },
    {
      key: '__showDeleted',
      label: 'common.data',
      type: 'switch',
      switchLabel: 'common.showDeletedData'
    }
  ];

  private readonly renterPaymentFilterFields: FilterField[] = [
    {
      key: 'txnNo',
      label: 'filter.transactionNo',
      type: 'text',
      placeholder: 'placeholder.typeTransactionNo',
      numericOnly: true,
      exactMatch: true
    },
    {
      key: 'paymentYear',
      label: 'filter.paymentYear',
      type: 'select',
      placeholder: 'placeholder.selectYear',
      dropdownObj: {
        name: 'paymentYear',
        valueKey: 'value',
        display1: 'label',
        label: 'filter.paymentYear',
        options: this.yearOptions,
        isSearchable: true
      }
    },
    {
      key: 'paymentMonth',
      label: 'filter.paymentMonth',
      type: 'select',
      placeholder: 'placeholder.selectMonth',
      dropdownObj: {
        name: 'paymentMonth',
        valueKey: 'value',
        display1: 'label',
        label: 'filter.paymentMonth',
        options: this.monthOptions,
        isSearchable: true,
        searchReadonly: true
      }
    },
    {
      key: 'paymentDate',
      label: 'filter.paymentDateRange',
      type: 'dateRange'
    },
    {
      key: 'createdOn',
      label: 'filter.createdOnRange',
      type: 'dateRange'
    }
  ];

  expenseFilterFields: FilterField[] = [
    {
      key: 'house.id',
      label: 'filter.house',
      type: 'select',
      placeholder: 'placeholder.selectHouse',
      dropdownObj: {
        apiInfo: 'HOUSE_LIST',
        name: 'house.id',
        valueKey: 'id',
        display1: 'houseName',
        display2: 'houseAddress',
        label: 'filter.house',
        placeholder: 'placeholder.selectHouse',
        isSearchable: true,
        status: 1
      }
    },
    {
      key: 'flat.id',
      label: 'filter.flat',
      type: 'select',
      placeholder: 'placeholder.selectFlat',
      dependsOnKey: 'house.id',
      dependsOnFilterKey: 'house.id',
      dropdownObj: {
        apiInfo: 'FLAT_LIST',
        name: 'flat.id',
        valueKey: 'id',
        display1: 'flatName',
        display2: 'floorName',
        label: 'filter.flat',
        placeholder: 'placeholder.selectFlat',
        isSearchable: true,
        status: 1
      }
    },
    {
      key: 'expenseHead.id',
      label: 'filter.expenseHead',
      type: 'select',
      placeholder: 'placeholder.selectExpenseHead',
      dropdownObj: {
        apiInfo: 'EXPENSE_HEAD_LIST',
        name: 'expenseHead.id',
        valueKey: 'id',
        display1: 'name',
        label: 'filter.expenseHead',
        placeholder: 'placeholder.selectExpenseHead',
        isSearchable: true
      }
    },
    {
      key: 'expenseYear',
      label: 'filter.expenseYear',
      type: 'select',
      placeholder: 'placeholder.selectYear',
      dropdownObj: {
        name: 'expenseYear',
        valueKey: 'value',
        display1: 'label',
        label: 'filter.expenseYear',
        options: this.yearOptions,
        isSearchable: true
      }
    },
    {
      key: 'expenseMonth',
      label: 'filter.expenseMonth',
      type: 'select',
      placeholder: 'placeholder.selectMonth',
      dropdownObj: {
        name: 'expenseMonth',
        valueKey: 'value',
        display1: 'label',
        label: 'filter.expenseMonth',
        options: this.monthOptions,
        isSearchable: true
      }
    },
    {
      key: 'expenseDate',
      label: 'filter.expenseDateRange',
      type: 'dateRange'
    },
    {
      key: 'createdOn',
      label: 'filter.createdOnRange',
      type: 'dateRange'
    },
    {
      key: '__showDeleted',
      label: 'common.data',
      type: 'switch',
      switchLabel: 'common.showDeletedData'
    }
  ];

  selectTab(tab: PaymentTab): void {
    if (!this.canShowTab(tab)) {
      return;
    }

    if (this.activeTab !== tab) {
      if (tab === 'Transaction') {
        this.paymentFilterValues = this.defaultPaymentFilterValues();
        this.paymentFilterActive = false;
        this.paymentList?.setFilters(this.defaultPaymentListFilters());
      } else {
        this.expenseFilterValues = { __showDeleted: false };
        this.expenseFilterActive = false;
        this.expenseList?.setFilters({ status: 1 });
      }
    }
    this.activeTab = tab;
    this.showFilter = false;
  }

  toggleFilter(): void {
    this.showFilter = !this.showFilter;
  }

  canShowTab(tab: PaymentTab): boolean {
    return this.access.hasService(tab === 'Transaction' ? 'PAYMENT_LIST' : 'EXPENSE_LIST');
  }

  canShowCreateButton(): boolean {
    return this.activeTab === 'Transaction'
      ? this.access.hasService('PAYMENT_CREATE')
      : this.access.hasService('EXPENSE_CREATE');
  }

  isFilterActive(): boolean {
    return this.activeTab === 'Transaction' ? this.paymentFilterActive : this.expenseFilterActive;
  }

  applyFilters(values: Record<string, any>, isActive = false): void {
    this.showFilter = false;
    if (this.activeTab === 'Transaction') {
      const sanitized = this.sanitizeFilterPayload(values);
      this.paymentFilterValues = this.mergeRenterPaymentConstraint(values);
      this.paymentFilterActive = isActive;
      this.paymentList?.setFilters(sanitized);
      return;
    }

    const sanitized = this.sanitizeGenericPayload(values);
    this.expenseFilterValues = { ...values };
    this.expenseFilterActive = isActive;
    this.expenseList?.setFilters(sanitized);
  }

  resetFilters(): void {
    if (this.activeTab === 'Transaction') {
      this.paymentFilterValues = this.defaultPaymentFilterValues();
      this.paymentFilterActive = false;
      this.paymentList?.setFilters(this.defaultPaymentListFilters());
      return;
    }

    this.expenseFilterValues = { __showDeleted: false };
    this.expenseFilterActive = false;
    this.expenseList?.setFilters({ status: 1 });
  }

  private sanitizeFilterPayload(values: Record<string, any>): Record<string, any> {
    const payload: Record<string, any> = {};
    const showDeleted = !!values?.['__showDeleted'];

    Object.keys(values || {}).forEach((key) => {
      if (key === '__showDeleted') {
        return;
      }
      const value = values[key];
      if (Array.isArray(value)) {
        const cleaned = value.map((item) => `${item ?? ''}`.trim()).filter((item) => item.length > 0);
        if (cleaned.length > 0) {
          payload[key] = cleaned;
        }
        return;
      }

      if (value === null || value === undefined) {
        return;
      }

      if (`${value}`.trim() === '') {
        return;
      }

      payload[key] = value;
    });

    payload['status'] = showDeleted ? 0 : 1;
    if (this.isRenterRole && this.renterId !== null) {
      payload['flatRenterMapping.renter.id'] = this.renterId;
    }

    return payload;
  }

  private sanitizeGenericPayload(values: Record<string, any>): Record<string, any> {
    const payload: Record<string, any> = {};
    const showDeleted = !!values?.['__showDeleted'];
    Object.keys(values || {}).forEach((key) => {
      if (key === '__showDeleted') {
        return;
      }
      const value = values[key];
      if (Array.isArray(value)) {
        const cleaned = value.map((item) => `${item ?? ''}`.trim()).filter((item) => item.length > 0);
        if (cleaned.length > 0) {
          payload[key] = cleaned;
        }
        return;
      }

      if (value === null || value === undefined) {
        return;
      }

      if (`${value}`.trim() === '') {
        return;
      }

      payload[key] = value;
    });

    payload['status'] = showDeleted ? 0 : 1;
    return payload;
  }

  openMakePayment(): void {
    if (this.isTransactionCreateBlocked()) {
      this.openLimitToast('Package expired. Please renew your package.');
      return;
    }
    this.paymentList?.openCreateModal();
  }

  openCreateExpense(): void {
    if (this.isTransactionCreateBlocked()) {
      this.openLimitToast('Package expired. Please renew your package.');
      return;
    }
    this.expenseList?.openCreateModal();
  }

  ngOnDestroy(): void {
    this.summaryRefreshSubscription?.unsubscribe();
  }

  private loadSubscriptionSummary(): void {
    this.subscriptionService.getSubscriptionSummary().subscribe({
      next: (summary) => {
        this.subscriptionSummary = summary;
      },
      error: () => {
        this.subscriptionSummary = null;
      }
    });
  }

  private isTransactionCreateBlocked(): boolean {
    const expireDate = `${this.subscriptionSummary?.currentPackage?.expireDate ?? ''}`.trim();
    if (!expireDate) {
      return false;
    }

    const today = new Date();
    const todayIso = `${today.getFullYear()}-${`${today.getMonth() + 1}`.padStart(2, '0')}-${`${today.getDate()}`.padStart(2, '0')}`;
    return expireDate < todayIso;
  }

  private openLimitToast(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 3500,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['dashboard-toast-warning']
    });
  }

  private defaultPaymentFilterValues(): Record<string, any> {
    return this.mergeRenterPaymentConstraint({ __showDeleted: false });
  }

  private defaultPaymentListFilters(): Record<string, any> {
    const filters: Record<string, any> = { status: 1 };
    if (this.isRenterRole && this.renterId !== null) {
      filters['flatRenterMapping.renter.id'] = this.renterId;
    }
    return filters;
  }

  private mergeRenterPaymentConstraint(values: Record<string, any>): Record<string, any> {
    const merged = { ...(values ?? {}) };
    if (this.isRenterRole && this.renterId !== null) {
      merged['flatRenterMapping.renter.id'] = this.renterId;
    }
    return merged;
  }
}
